import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useClaimBill } from '../billing/useClaimBill.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

export function InvoiceValidationPage(): ReactNode {
  const { t } = useTranslation();
  const { claimId } = useParams<{ claimId: string }>();
  const { token } = useAuth();
  const { bill, error: claimError } = useClaimBill(`/v1/approval/claim/${claimId}`);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ invoiceNumber: '', invoiceDate: '', gstNumber: '', vendorName: '', vendorAddress: '', invoiceAmount: '', taxAmount: '', totalAmount: '' });

  const handleValidate = async () => {
    try {
      const r = await apiRequest<any>('/v1/approval/invoice-validation/', {
        token, method: 'POST',
        body: { claimId, invoiceId: crypto.randomUUID(), ...form, invoiceAmount: Number(form.invoiceAmount), taxAmount: Number(form.taxAmount), totalAmount: Number(form.totalAmount) },
      });
      setResult(r);
    } catch (e) { setError((e as Error).message); }
  };

  const fields: Array<{ key: keyof typeof form; label: string; type?: 'date' | 'number' }> = [
    { key: 'invoiceNumber', label: t('fa.invoice.invoiceNumber') }, { key: 'invoiceDate', label: t('fa.invoice.invoiceDate'), type: 'date' },
    { key: 'gstNumber', label: t('fa.invoice.gstNumber') }, { key: 'vendorName', label: t('fa.invoice.vendorName') },
    { key: 'vendorAddress', label: t('fa.invoice.vendorAddress') }, { key: 'invoiceAmount', label: t('fa.invoice.invoiceAmount'), type: 'number' },
    { key: 'taxAmount', label: t('fa.invoice.taxAmount'), type: 'number' }, { key: 'totalAmount', label: t('fa.invoice.totalAmount'), type: 'number' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>{t('fa.invoice.title', { bill })}</h1>
      {(error ?? claimError) && <p style={{ color: 'red' }}>{error ?? claimError}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, maxWidth: 600, marginBottom: 16 }}>
        {fields.map(f => (
          <label key={f.key}>{f.label}<br />
            {f.type === 'date'
              ? <DateInput value={form[f.key]} onValueChange={nextValue => setForm({ ...form, [f.key]: nextValue })} style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
              : <input type={f.type ?? 'text'} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />}
          </label>
        ))}
      </div>
      <button onClick={handleValidate} style={{ padding: '8px 24px', background: 'var(--color-text-link)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 6, cursor: 'pointer' }}>{t('fa.invoice.validateBtn')}</button>
      {result && (
        <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--color-border-default)', borderRadius: 8 }}>
          <strong>{t('fa.invoice.result')}</strong> <span style={{ color: result.validationResult === 'PASS' ? 'var(--color-success)' : 'var(--color-danger)' }}>{result.validationResult}</span>
          {result.discrepancies?.issues && <ul>{result.discrepancies.issues.map((d: string, i: number) => <li key={i} style={{ color: 'var(--color-danger)' }}>{d}</li>)}</ul>}
        </div>
      )}
    </div>
  );
}
